/**
 * Heat layer math: normalized points → pixel space → gaussian blobs → one-hue
 * sequential ramp (dataviz rule: magnitude is one hue, light → dark; never a
 * rainbow). Pure functions are exported for tests; the canvas painter guards
 * environments without a 2D context (jsdom).
 */
import type { Heatmap } from "@backstory/protocol";

export type HeatPoint = NonNullable<Heatmap["points"]>[number];

export const BREAKPOINT_SIZE: Record<Heatmap["breakpoint"], { w: number; h: number }> = {
  desktop: { w: 1280, h: 800 },
  tablet: { w: 834, h: 1112 },
  mobile: { w: 390, h: 844 },
};

/** Sequential blue ramp (dataviz reference palette) from light (near zero) to dark. */
export const SEQ_RAMP = ["#cde2fb", "#b7d3f6", "#9ec5f4", "#86b6ef", "#6da7ec", "#5598e7", "#3987e5", "#2a78d6", "#256abf", "#1c5cab", "#184f95", "#104281", "#0d366b"];

export interface PixelPoint {
  x: number;
  y: number;
  w: number; // normalized 0..1 weight
  selector?: string;
}

/**
 * Scale normalized coordinates to the document.
 * X is a viewport fraction (0..1). Y is deliberately not clamped at 1: the
 * detector stores document depth in viewport units so interactions below the
 * fold remain below the fold.
 */
export function normalizePoints(points: HeatPoint[] | undefined, width: number, height: number): PixelPoint[] {
  if (!points || points.length === 0) return [];
  const maxW = Math.max(...points.map((p) => (Number.isFinite(p.w) ? p.w : 0)), 1e-9);
  return points
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
    .map((p) => ({
      x: Math.min(1, Math.max(0, p.x)) * width,
      y: Math.max(0, p.y) * height,
      w: Math.min(1, Math.max(0, (Number.isFinite(p.w) ? p.w : 0) / maxW)),
      selector: p.selector,
    }));
}

/** Height needed to include recorded interactions, capped against bad data. */
export function heatmapDocumentHeight(points: HeatPoint[] | undefined, viewportHeight: number): number {
  const maxDepth = Math.min(20, Math.max(1, ...(points ?? []).filter((p) => Number.isFinite(p.y)).map((p) => Math.max(0, p.y))));
  return maxDepth <= 1 ? viewportHeight : Math.ceil((maxDepth + 0.08) * viewportHeight);
}

/** Map intensity 0..1 to a ramp step. */
export function rampColor(intensity: number, ramp: string[] = SEQ_RAMP): string {
  const i = Math.min(ramp.length - 1, Math.max(0, Math.floor(intensity * (ramp.length - 1))));
  return ramp[i]!;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/**
 * Paint gaussian blobs into an offscreen alpha accumulation, then colorize by
 * the ramp. Returns false when no 2D context is available (test environments).
 */
export function paintHeat(canvas: HTMLCanvasElement, points: PixelPoint[], opts: { radius?: number; maxAlpha?: number } = {}): boolean {
  const ctx = canvas.getContext("2d");
  if (!ctx) return false;
  const { width, height } = canvas;
  ctx.clearRect(0, 0, width, height);
  if (points.length === 0) return true;
  const radius = opts.radius ?? Math.max(18, Math.round(Math.min(width, height) / 24));

  // 1. accumulate alpha with radial gradients
  const acc = document.createElement("canvas");
  acc.width = width;
  acc.height = height;
  const actx = acc.getContext("2d");
  if (!actx) return false;
  for (const p of points) {
    const g = actx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius);
    const a = 0.25 + 0.75 * p.w;
    g.addColorStop(0, `rgba(0,0,0,${a.toFixed(3)})`);
    g.addColorStop(1, "rgba(0,0,0,0)");
    actx.fillStyle = g;
    actx.fillRect(p.x - radius, p.y - radius, radius * 2, radius * 2);
  }

  // 2. colorize: alpha → ramp step
  const img = actx.getImageData(0, 0, width, height);
  const data = img.data;
  const rgb = SEQ_RAMP.map(hexToRgb);
  let maxA = 0;
  for (let i = 3; i < data.length; i += 4) if (data[i]! > maxA) maxA = data[i]!;
  const maxAlpha = opts.maxAlpha ?? 0.85;
  if (maxA === 0) return true;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3]! / maxA;
    if (a <= 0.02) {
      data[i + 3] = 0;
      continue;
    }
    const [r, g, b] = rgb[Math.min(rgb.length - 1, Math.floor(a * (rgb.length - 1)))]!;
    data[i] = r;
    data[i + 1] = g;
    data[i + 2] = b;
    data[i + 3] = Math.round(Math.min(1, a) * maxAlpha * 255);
  }
  ctx.putImageData(img, 0, 0);
  return true;
}

/** Aggregate per-element counts by selector for hover overlays. */
export function elementIndex(hm: Heatmap): Map<string, { clicks: number; rage: number; dead: number }> {
  const m = new Map<string, { clicks: number; rage: number; dead: number }>();
  for (const e of hm.elements ?? []) m.set(e.selector, { clicks: e.clicks, rage: e.rage, dead: e.dead });
  return m;
}

/** Scroll depth buckets → sorted, clamped percentages. */
export function scrollBuckets(hm: Heatmap): Array<{ depthPct: number; sessionsPct: number }> {
  return (hm.scrollDepth ?? [])
    .map((b) => ({ depthPct: Math.min(100, Math.max(0, b.depthPct)), sessionsPct: Math.min(100, Math.max(0, b.sessionsPct)) }))
    .sort((a, b) => a.depthPct - b.depthPct);
}
